import { useMemo } from 'react';
import { palette, admin } from '../../styles/tokens';
import { canonicalMoney, sumMoney } from '../../lib/money';
import type { BookingRow } from './hooks';

interface Resource {
  id: string;
  name: string;
  assigned_user_id: string | null;
  assigned_name?: string;
}

interface BusinessHours { open_time: string; close_time: string }

interface Props {
  bookings: BookingRow[];
  resources: Resource[];
  businessHours: BusinessHours;
  date: string;
}

function timeToMin(t: string): number { const [h, m] = t.split(':').map(Number); return h * 60 + (m || 0); }

export function StatsPanel({ bookings, resources, businessHours, date }: Props) {
  const dayBookings = useMemo(() => bookings.filter(b => b.date === date && b.status !== 'cancelled'), [bookings, date]);

  const stats = useMemo(() => {
    const pending = dayBookings.filter(b => b.status === 'pending').length;
    const walkIns = dayBookings.filter(b => b.source === 'walk_in').length;
    const revenue = Number(sumMoney(dayBookings
      .filter(b => ['confirmed', 'in_progress', 'completed'].includes(b.status))
      .map(b => canonicalMoney(b.payment_amount ?? 0))));

    // Next available: find earliest gap across all chairs
    const openMin = timeToMin(businessHours.open_time);
    const closeMin = timeToMin(businessHours.close_time);
    let nextAvail = '';
    for (const r of resources) {
      const booked = dayBookings
        .filter(b => b.assigned_id === r.assigned_user_id)
        .map(b => ({ s: timeToMin(b.start_time), e: timeToMin(b.end_time) }))
        .sort((a, b) => a.s - b.s);
      const nowMin = new Date().getHours() * 60 + new Date().getMinutes();
      let cursor = Math.max(openMin, nowMin);
      for (const slot of booked) {
        if (cursor < slot.s) { nextAvail = `${Math.floor(cursor / 60) % 12 || 12}:${String(cursor % 60).padStart(2, '0')} ${cursor >= 720 ? 'PM' : 'AM'} · ${r.assigned_name ?? r.name}`; break; }
        cursor = Math.max(cursor, slot.e);
      }
      if (!nextAvail && cursor < closeMin) {
        nextAvail = `${Math.floor(cursor / 60) % 12 || 12}:${String(cursor % 60).padStart(2, '0')} ${cursor >= 720 ? 'PM' : 'AM'} · ${r.assigned_name ?? r.name}`;
      }
      if (nextAvail) break;
    }

    return { total: dayBookings.length, pending, walkIns, revenue, nextAvail: nextAvail || 'No availability' };
  }, [dayBookings, resources, businessHours]);

  /* eslint-disable react-hooks/static-components */
  const Metric = ({ label, value, accent }: { label: string; value: string | number; accent?: string }) => (
    <div style={{ marginBottom: 16 }}>
      <div style={{ fontSize: 11, fontWeight: 500, color: palette.g500, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 600, color: accent ?? palette.g800, letterSpacing: '-0.03em' }}>{value}</div>
    </div>
  );

  return (
    <div style={{ width: 200, minWidth: 200, padding: 16, borderRight: `1px solid ${admin.border}`, background: palette.g50 }}>
      <Metric label="Today" value={stats.total} />
      <Metric label="Est. Revenue" value={`$${stats.revenue.toFixed(0)}`} accent={palette.green} />
      <Metric label="Next Available" value={stats.nextAvail} />
      {stats.pending > 0 && <Metric label="Pending" value={stats.pending} accent={palette.amber} />}
      {stats.walkIns > 0 && <Metric label="Walk-ins" value={stats.walkIns} />}
    </div>
  );
  /* eslint-enable react-hooks/static-components */
}
